/**
 * Cross-link index between the public Playbooks feed and the Toolbox feed.
 *
 * Each Toolbox entry carries `used_in_guides: string[]` with composite IDs
 * like `tech_sales_tech_sales_2026_06`. These don't match playbook slugs
 * 1:1, so we fuzzy-match by token overlap against `playbook.slug + title`.
 *
 * Exposes two bidirectional indexes:
 *   - toolToPlaybooks:  tool index → playbook[]
 *   - playbookToTools:  playbook.slug → { tool, toolIdx }[]
 */
import { getPlaybooksFeed, getToolboxFeed } from "@/lib/api";

const STOP = new Set([
  "all", "guides", "guide", "funnel", "page", "of", "to", "the", "for", "in",
  "a", "and", "an", "with", "tools", "tool", "from", "your",
  // year/date tail tokens we see in guide_ids
  "2024", "2025", "2026", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12",
]);

function tokens(s) {
  if (!s) return new Set();
  const out = new Set();
  for (const t of String(s).toLowerCase().match(/[a-z]+/g) || []) {
    if (t.length > 2 && !STOP.has(t)) out.add(t);
  }
  return out;
}

let cachePromise = null;

/** Returns `{ playbooks, tools, toolToPlaybooks, playbookToTools }`. Memoized. */
export function getCrossLinks() {
  if (cachePromise) return cachePromise;
  cachePromise = (async () => {
    const [pbRes, tbRes] = await Promise.all([getPlaybooksFeed(), getToolboxFeed()]);
    const playbooks = pbRes.ok && Array.isArray(pbRes.data) ? pbRes.data : [];
    const tools = tbRes.ok && Array.isArray(tbRes.data) ? tbRes.data : [];

    const pbIndex = playbooks.map((p) => ({
      pb: p,
      tok: tokens(`${p.slug} ${p.title} ${(p.keywords || []).join(" ")}`),
    }));

    const guideCache = new Map();
    const matchGuide = (guideId) => {
      if (!guideId || guideId.startsWith("__")) return [];
      if (guideCache.has(guideId)) return guideCache.get(guideId);
      const gtok = tokens(guideId);
      if (gtok.size === 0) {
        guideCache.set(guideId, []);
        return [];
      }
      const scored = [];
      for (const { pb, tok } of pbIndex) {
        let inter = 0;
        for (const t of gtok) if (tok.has(t)) inter += 1;
        if (inter >= 2) scored.push({ pb, score: inter });
      }
      scored.sort((a, b) => b.score - a.score);
      const top = scored.slice(0, 3).map((x) => x.pb);
      guideCache.set(guideId, top);
      return top;
    };

    const toolToPlaybooks = new Map();
    const playbookToTools = new Map();

    tools.forEach((t, i) => {
      const guides = t.used_in_guides || [];
      const matched = new Map();
      for (const g of guides) for (const pb of matchGuide(g)) matched.set(pb.slug, pb);
      const arr = Array.from(matched.values());
      toolToPlaybooks.set(i, arr);
      for (const pb of arr) {
        if (!playbookToTools.has(pb.slug)) playbookToTools.set(pb.slug, []);
        playbookToTools.get(pb.slug).push({ tool: t, toolIdx: i });
      }
    });

    return { playbooks, tools, toolToPlaybooks, playbookToTools };
  })();
  return cachePromise;
}
